import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProductService } from '../../core/services/product.service';
import { BehaviorSubject, combineLatest, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { ApiResource } from '../../shared/model/api-resource.model';
import { ProductDetail } from '../../shared/model/product.model';
import { Destroyed } from '../../shared/decorators/destroyed';
import { BreadcrumbsService } from '../../core/services/breadcrumbs.service';
import { Layout } from '../../shared/model/layout.model';
import { CmsService } from '../../core/services/cms.service';
import { CartService } from '../../core/services/cart.service';
import { SeoService } from '../../core/services/seo.service';
import { Page } from '../../shared/components/layout/page/page.component';
import { TranslocoService } from '@ngneat/transloco';
import { SsrService } from '../../core/services/ssr.service';
import { LayoutService } from '../../core/services/layout.service';
import { GtmService } from '../../core/services/gtm.service';

@Component({
  selector: 'raf-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductDetailComponent extends Page implements OnInit, OnDestroy {
  productResource$: Observable<ApiResource<ProductDetail>> | null = null;
  product$: BehaviorSubject<ProductDetail | null> = new BehaviorSubject<ProductDetail | null>(null);
  productLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  layout$: Observable<Layout | null>;
  selectedQuantity$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  tablet$?: Observable<boolean>;

  @Destroyed
  destroyed$!: Subject<void>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private productService: ProductService,
    private cmsService: CmsService,
    private cartService: CartService,
    private layoutService: LayoutService,
    protected override breadcrumbService: BreadcrumbsService,
    protected override translocoService: TranslocoService,
    protected override seoService: SeoService,
    protected override ssrService: SsrService,
    private gtm: GtmService,
    @Inject(PLATFORM_ID) private platformId: object
  ) {
    super(translocoService, breadcrumbService, seoService, ssrService);
    this.layout$ = this.cmsService.layout$.pipe(map((resource) => resource.data));
    this.tablet$ = this.layoutService.lgDown$;
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroyed$)).subscribe((params) => {
      if (params['product']) {
        this.getProduct(params['product']);
      }
    });

    this.layoutService.enableSmoothScroll();
  }

  getProduct(productId: string): void {
    this.hideFullPageError();
    this.productResource$ = this.productService.getProductDetail(productId);
    this.productResource$.pipe(takeUntil(this.destroyed$)).subscribe((product) => {
      if (product.error) {
        this.showFullPageError(404); // fixme pri jakekoliv chybe ukazat 404?
        this.productLoading$.next(false);
        return;
      }

      this.productLoading$.next(product.loading);
      this.product$.next(product.data);
      if (product.data) {
        this.ensureCorrectUrl(product.data);
        if (product.data.breadcrumbs) {
          this.breadcrumbService.setBreadcrumbs(
            product.data.breadcrumbs
              .filter((item, idx) => idx > 0) // filter root
              .map((item) => ({ path: item.url, label: item.name || '' }))
          );
        } else {
          this.breadcrumbService.setBreadcrumbs([{ path: '.', label: product.data?.name || '' }]);
        }
        this.setSeo(product.data);
        this.logGtm(product.data);
        this.selectedQuantity$.next(product.data.moq);

        // once loading is finished and detail becomes visible, use url fragment to scroll
        if (!product.loading) {
          this.scrollToFragmentByUrl();
        }
      }
    });
  }

  onAddToCart() {
    combineLatest([this.product$, this.selectedQuantity$])
      .pipe(take(1))
      .subscribe(([product, quantity]) => {
        if (product) {
          this.cartService.addToCart(product, quantity);
        }
      });
  }

  onQuantityChange(quantity: number) {
    this.selectedQuantity$.next(quantity);
  }

  scrollToFragmentByUrl(): void {
    // wait for view to render, then find the element and scroll to it
    const fragment = this.route.snapshot.fragment;
    if (fragment) {
      this.scrollTo(fragment, 150);
    }
  }

  scrollTo(elementId: string, timeout = 0): void {
    // timeout to override default angular scrolling to top on every URL change
    setTimeout(() => {
      this.layoutService.scrollTo(elementId);
    }, timeout);
  }

  private ensureCorrectUrl(product: ProductDetail) {
    const url = this.router.createUrlTree([product.productUrl], { queryParamsHandling: 'merge' }).toString();
    this.ssrService.ensureCorrectUrl(url);
  }

  private setSeo(product: ProductDetail): void {
    this.seoService.setMetaTagsForProduct(product);
    this.seoService.setTitle(product.name);
  }

  private logGtm(item: ProductDetail): void {
    this.gtm.viewItem(item);
  }

  ngOnDestroy() {
    this.seoService.clearMetaTagsForProduct();
    this.layoutService.disableSmoothScroll();
  }
}
